import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useConsentState, consentRedirect } from "@/lib/consentGate";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Loader2,
  Clock,
  AlertCircle,
  LifeBuoy,
  Mail,
  Download,
} from "lucide-react";
import WeKitLogo from "@/components/WeKitLogo";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  clearPendingPayment,
  getPendingPayment,
} from "@/lib/pendingPayment";
import { generateReceiptPdf } from "@/lib/paymentReceipt";
import { track } from "@/lib/analytics";
import { ga4Purchase, ga4Once } from "@/lib/ga4";


type PaymentRow = {
  id: string;
  payment_status: string | null;
  razorpay_payment_id: string | null;
  razorpay_order_id: string | null;
  paypal_order_id: string | null;
  paypal_capture_id: string | null;
  payment_provider: string | null;
  currency: string | null;
  list_price: number | null;
  discount_applied: number | null;
  final_price: number | null;
  scholarship_code: string | null;
  user_name: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  school: string | null;
  created_at: string;
  updated_at: string | null;
};

const TOTAL_ATTEMPTS = 20; // ~40s @ 2s
const POLL_INTERVAL_MS = 2000;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [confirmed, setConfirmed] = useState(false);
  const [syncTimedOut, setSyncTimedOut] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [lastKnown, setLastKnown] = useState<PaymentRow | null>(null);

  const pending = getPendingPayment();
  const orderId =
    pending?.orderId || localStorage.getItem("wekit_payment_order") || null;

  const progressPct = Math.min(
    100,
    Math.round((attempt / TOTAL_ATTEMPTS) * 100),
  );

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    const fetchLatest = async (): Promise<PaymentRow | null> => {
      let q = supabase
        .from("payments")
        .select(
          "id, payment_status, razorpay_payment_id, razorpay_order_id, paypal_order_id, paypal_capture_id, payment_provider, currency, list_price, discount_applied, final_price, scholarship_code, user_name, email, phone, city, school, created_at, updated_at",
        )
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1);
      if (orderId) q = q.or(`razorpay_order_id.eq.${orderId},paypal_order_id.eq.${orderId}`);
      const { data } = await q;
      return (data?.[0] as PaymentRow | undefined) ?? null;
    };

    const poll = async () => {
      for (let i = 0; i < TOTAL_ATTEMPTS; i++) {
        if (cancelled) return;
        setAttempt(i + 1);
        const row = await fetchLatest();
        if (cancelled) return;
        if (row) setLastKnown(row);
        if (row?.payment_status === "success") {
          clearPendingPayment();
          setConfirmed(true);
          return;
        }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      }
      if (!cancelled) {
        // Final read so the user sees the most recent backend status.
        const finalRow = await fetchLatest();
        if (!cancelled) {
          if (finalRow) setLastKnown(finalRow);
          setSyncTimedOut(true);
        }
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [user?.id, orderId]);

  const firstName = (() => {
    const raw =
      lastKnown?.user_name ||
      (user?.user_metadata as { full_name?: string } | undefined)?.full_name ||
      "";
    const first = raw.trim().split(/\s+/)[0];
    return first ? first.charAt(0).toUpperCase() + first.slice(1) : null;
  })();

  const receiptEmail = lastKnown?.email || user?.email || null;

  // Analytics: page view once, and confirmation when the webhook lands.
  useEffect(() => {
    track("payment_success_view", { surface: "payment-success" });
  }, []);

  useEffect(() => {
    if (!confirmed) return;
    track("payment_success_confirmed", {
      surface: "payment-success",
      props: {
        provider: lastKnown?.payment_provider ?? null,
        currency: lastKnown?.currency ?? null,
        final_price: lastKnown?.final_price ?? null,
      },
    });
  }, [confirmed, lastKnown?.payment_provider, lastKnown?.currency, lastKnown?.final_price]);

  // GA4 purchase: only once the backend confirms success; deduped per transaction.
  useEffect(() => {
    if (!confirmed || lastKnown?.payment_status !== "success") return;
    const tx = lastKnown.paypal_capture_id || lastKnown.paypal_order_id ||
      lastKnown.razorpay_payment_id || lastKnown.razorpay_order_id || lastKnown.id;
    ga4Purchase(tx, Number(lastKnown.final_price ?? 0), lastKnown.currency || "INR");
  }, [confirmed, lastKnown]);

  // Auto-redirect once confirmed. DPDP Act: for a learner under 18, payment
  // success must not lead into the assessment — the next step is guardian
  // verification and explicit consent.
  const { state: consent, loading: consentLoading } = useConsentState();
  const nextStop = consentRedirect(consent) ?? "/start";
  const needsConsent = nextStop !== "/start";
  const [countdown, setCountdown] = useState(6);
  useEffect(() => {
    if (confirmed && !consentLoading && needsConsent) ga4Once("parent_consent_started", { source: "payment-success" });
  }, [confirmed, consentLoading, needsConsent]);
  useEffect(() => {
    if (!confirmed || consentLoading) return;
    const tick = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    const go = setTimeout(() => {
      track("payment_success_redirect", {
        surface: "payment-success",
        props: { to: nextStop },
      });
      navigate(nextStop, { replace: true });
    }, 6000);
    return () => {
      clearInterval(tick);
      clearTimeout(go);
    };
  }, [confirmed, consentLoading, nextStop, navigate]);




  const formattedAmount =
    lastKnown?.final_price != null
      ? new Intl.NumberFormat("en-IN", {
          style: "currency",
          currency: "INR",
          maximumFractionDigits: 0,
        }).format(Number(lastKnown.final_price))
      : null;

  const statusLabel = (s?: string | null) => {
    switch ((s || "").toLowerCase()) {
      case "success":
        return "Confirmed";
      case "pending":
        return "Awaiting confirmation";
      case "failed":
        return "Failed";
      case "created":
        return "Created — awaiting capture";
      default:
        return s || "Unknown";
    }
  };

  const handleDownloadReceipt = () => {
    if (!lastKnown) return;
    generateReceiptPdf({
      userName: lastKnown.user_name,
      email: lastKnown.email,
      phone: lastKnown.phone,
      city: lastKnown.city,
      school: lastKnown.school,
      provider: lastKnown.payment_provider || "razorpay",
      orderId: lastKnown.razorpay_order_id,
      paymentId: lastKnown.razorpay_payment_id,
      paypalOrderId: lastKnown.paypal_order_id,
      paypalCaptureId: lastKnown.paypal_capture_id,
      currency: lastKnown.currency,
      listPrice: lastKnown.list_price,
      discountApplied: lastKnown.discount_applied,
      finalPrice: lastKnown.final_price,
      couponCode: lastKnown.scholarship_code,
      status: lastKnown.payment_status,
      paidAt: lastKnown.updated_at || lastKnown.created_at,
      receiptNumber: lastKnown.id,
    });
  };

  // Payment confirmed — thank the learner by name, point them at their inbox,
  // then walk them into the assessment automatically.
  if (confirmed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <Card className="border-primary/20 bg-card/80 backdrop-blur text-center">
            <CardContent className="pt-10 pb-8 px-6 space-y-6">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.15 }}
                className="mx-auto w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center"
              >
                <CheckCircle2 className="h-10 w-10 text-primary" aria-hidden="true" />
              </motion.div>

              <div>
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-2">
                  {firstName ? `Thank you, ${firstName}!` : "Thank you!"}
                </h1>
                <p className="text-base text-primary font-semibold">
                  You're in. Welcome to WeKIT™
                </p>
                {formattedAmount && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {formattedAmount} received — your full assessment is unlocked.
                  </p>
                )}
              </div>

              <div
                className="flex items-start gap-3 rounded-2xl border border-border bg-muted/30 p-4 text-left"
                role="status"
              >
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Your receipt is on its way to{" "}
                  {receiptEmail ? (
                    <span className="text-foreground font-medium break-all">{receiptEmail}</span>
                  ) : (
                    "the email address you entered"
                  )}
                  , along with your welcome guide. If it isn't there in a few minutes, check spam or
                  promotions — and you can always download it below.
                </p>

              </div>

              {lastKnown?.scholarship_code === "WCC3604INDIA" && (
                <div className="text-left rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-5 space-y-3">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold">
                    Leadership Preview
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Thank you for exploring WeKIT™. This complimentary access is for leaders
                    shaping the future of education, talent and workforce development. We'd love
                    your feedback.
                  </p>
                  <div className="pt-2 border-t border-border/40">
                    <div className="text-sm font-semibold text-foreground">Yvonne V. Roberts</div>
                    <div className="text-xs text-muted-foreground">Founder &amp; CEO, WeKIT™</div>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {needsConsent && (
                  <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4 text-left">
                    <p className="text-sm font-semibold text-foreground">
                      One final step: open their Career Passport
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Because {consent.childName ?? "your child"} is under 18, we
                      need to verify that you're their parent or guardian and get
                      your consent before we begin processing their assessment
                      information.
                    </p>
                  </div>
                )}
                <Button
                  onClick={() => {
                    track("payment_success_redirect", {
                      surface: "payment-success",
                      props: { to: nextStop, manual: true },
                    });
                    navigate(nextStop, { replace: true });
                  }}
                  className="w-full h-12 text-base font-semibold"
                  size="lg"
                >
                  <Sparkles className="h-5 w-5" aria-hidden="true" />
                  {needsConsent ? "Verify & give parental consent" : "Start my assessment"}
                  <ArrowRight className="h-5 w-5" aria-hidden="true" />
                </Button>
                <p className="text-xs text-muted-foreground" aria-live="polite">
                  Taking you there automatically in {countdown}s…
                </p>


                {lastKnown?.payment_status === "success" && (
                  <Button variant="outline" className="w-full" onClick={handleDownloadReceipt}>
                    <Download className="h-4 w-4" aria-hidden="true" />
                    Download payment receipt (PDF)
                  </Button>
                )}
              </div>

              <div className="pt-4 border-t border-border/50">
                <WeKitLogo size="sm" />
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }



  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <Card className="border-primary/20 bg-card/80 backdrop-blur text-center">
          <CardContent className="pt-10 pb-8 px-6 space-y-6">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.15 }}
              className="mx-auto w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center"
            >
              {confirmed ? (
                <CheckCircle2 className="h-10 w-10 text-primary" />
              ) : syncTimedOut ? (
                <Clock className="h-10 w-10 text-primary" />
              ) : (
                <Loader2 className="h-10 w-10 text-primary animate-spin" />
              )}
            </motion.div>

            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-2">
                {confirmed
                  ? "Payment Successful"
                  : syncTimedOut
                    ? "Still confirming your payment"
                    : "Confirming your payment"}
              </h1>
              <p className="text-base text-primary font-semibold">
                {confirmed
                  ? "Welcome to WeKIT™"
                  : syncTimedOut
                    ? "We haven't received final confirmation yet"
                    : "This usually takes just a few seconds"}
              </p>
            </div>

            {/* Live progress while polling */}
            {!confirmed && !syncTimedOut && (
              <div className="space-y-2 text-left">
                <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                  <motion.div
                    className="h-full bg-primary"
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPct}%` }}
                    transition={{ duration: 0.4 }}
                  />
                </div>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                    Payment captured by Razorpay
                  </li>
                  <li className="flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 text-primary animate-spin" />
                    Waiting for secure webhook confirmation…
                  </li>
                  <li className="flex items-center gap-2 opacity-60">
                    <div className="h-3.5 w-3.5 rounded-full border border-muted-foreground/40" />
                    Unlock your assessment
                  </li>
                </ul>
                <p className="text-xs text-muted-foreground pt-1">
                  Please don't close this window.
                </p>
              </div>
            )}

            {/* Success copy */}
            {confirmed && lastKnown?.scholarship_code === "WCC3604INDIA" ? (
              <div className="text-left rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-5 space-y-3">
                <div className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold">
                  Leadership Preview
                </div>
                <h2 className="font-display text-xl font-bold text-foreground leading-snug">
                  Welcome to WeKIT™ Career Clarity 360
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Thank you for taking the time to explore WeKIT™. This complimentary access
                  has been created for leaders shaping the future of education, talent,
                  innovation, and workforce development.
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Our vision is to build the Human Development Intelligence infrastructure for
                  the AI era — helping every learner discover purpose while giving institutions
                  actionable Career Intelligence. We hope you enjoy the experience and welcome
                  your feedback.
                </p>
                <div className="pt-2 border-t border-border/40">
                  <div className="text-sm font-semibold text-foreground">Yvonne V. Roberts</div>
                  <div className="text-xs text-muted-foreground">Founder &amp; CEO, WeKIT™</div>
                </div>
              </div>
            ) : confirmed && (
              <p className="text-muted-foreground text-sm">
                You now have access to the full AI Career Discovery Assessment.
                <br />
                Redirecting you to begin your assessment.
              </p>
            )}

            {/* Timeout: show last-known status + helpful next steps */}
            {syncTimedOut && !confirmed && (
              <div className="space-y-4 text-left">
                <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <AlertCircle className="h-4 w-4 text-primary" />
                    Current payment status
                  </div>
                  <dl className="text-xs text-muted-foreground grid grid-cols-3 gap-y-1.5">
                    <dt>Status</dt>
                    <dd className="col-span-2 text-foreground font-medium">
                      {statusLabel(lastKnown?.payment_status)}
                    </dd>
                    {orderId && (
                      <>
                        <dt>Order ID</dt>
                        <dd className="col-span-2 font-mono break-all">
                          {orderId}
                        </dd>
                      </>
                    )}
                    {lastKnown?.razorpay_payment_id && (
                      <>
                        <dt>Payment ID</dt>
                        <dd className="col-span-2 font-mono break-all">
                          {lastKnown.razorpay_payment_id}
                        </dd>
                      </>
                    )}
                    {formattedAmount && (
                      <>
                        <dt>Amount</dt>
                        <dd className="col-span-2 text-foreground font-medium">
                          {formattedAmount}
                        </dd>
                      </>
                    )}
                  </dl>
                </div>

                <div className="text-xs text-muted-foreground space-y-2">
                  <p className="font-medium text-foreground">What to do next</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>
                      If you were charged, the confirmation usually arrives
                      within a few minutes — refresh to check again.
                    </li>
                    <li>
                      Don't pay again — your bank statement will show the
                      original charge if it succeeded.
                    </li>
                    <li>
                      Still stuck? Email{" "}
                      <a
                        href={`mailto:support@wekit.ai?subject=Payment%20pending%20confirmation&body=Order%20ID%3A%20${encodeURIComponent(
                          orderId || "",
                        )}%0APayment%20ID%3A%20${encodeURIComponent(
                          lastKnown?.razorpay_payment_id || "",
                        )}`}
                        className="text-primary underline underline-offset-2"
                      >
                        support@wekit.ai
                      </a>{" "}
                      with the order ID above.
                    </li>
                  </ul>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Button
                onClick={() => navigate("/start", { replace: true })}
                className="w-full h-12 text-base font-semibold"
                size="lg"
                disabled={!confirmed}
              >
                <Sparkles className="h-5 w-5" />
                Start Assessment
                <ArrowRight className="h-5 w-5" />
              </Button>

              {lastKnown?.payment_status === "success" && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => handleDownloadReceipt()}
                >
                  <Download className="h-4 w-4" />
                  Download payment receipt (PDF)
                </Button>
              )}




              {syncTimedOut && !confirmed && (
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    onClick={() => window.location.reload()}
                  >
                    Refresh
                  </Button>
                  <Button variant="ghost" asChild>
                    <a href="mailto:support@wekit.ai?subject=Payment%20pending%20confirmation">
                      <LifeBuoy className="h-4 w-4" />
                      Contact support
                    </a>
                  </Button>
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-border/50">
              <WeKitLogo size="sm" />
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
